<template>
  <div class="selection-stats-panel">
    <div class="selection-stats">
      <span class="selection-stat">
        <span class="material-icons">straighten</span>
        {{ formattedDistance }}
      </span>
      <span class="selection-stat">
        <span class="material-icons">trending_up</span>
        {{ formattedGain }}
      </span>
      <span class="selection-stat">
        <span class="material-icons">trending_down</span>
        {{ formattedLoss }}
      </span>
      <span class="selection-stat">
        <span class="material-icons">show_chart</span>
        {{ formattedAvgGradient }}
      </span>
      <span class="selection-stat">
        <span class="material-icons">schedule</span>
        {{ formattedDuration }}
      </span>
    </div>
    <button type="button" class="selection-reset" title="Clear selection" @click="$emit('reset')">
      <span class="material-icons">close</span>
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * Replaces the panel header's normal stat cluster while a range selection is
 * active — the PRIMARY presentation for selection stats (not a floating
 * overlay), because it's the only placement that's robust on mobile: nothing
 * renders under the user's thumb, since the header sits at the top of the
 * sheet/panel regardless of where the selection itself is on the chart.
 */
import { computed } from 'vue';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { PrettyTime } from '../../../utils/helpers/HikeTime';
import type { RangeStats } from '../../../utils/elevation/rangeStats';

const props = withDefaults(
  defineProps<{
    selection: RangeStats;
    measurementPref?: MeasurementPrefUtility | null;
  }>(),
  { measurementPref: null }
);

defineEmits<{ (e: 'reset'): void }>();

const pref = computed(() => props.measurementPref ?? MeasurementPrefUtility.preference());

// 1dp, matching the whole-route header readout this replaces while a range
// is selected — mixed precision in the same header slot reads as a glitch.
const formattedDistance = computed(() => pref.value.bigUnitFormatter(props.selection.distanceMeters / 1000, true, 1));
const formattedGain = computed(() => pref.value.smallUnitFormatter(props.selection.gainMeters, false));
const formattedLoss = computed(() => pref.value.smallUnitFormatter(props.selection.lossMeters, false));
const formattedAvgGradient = computed(() => {
  const rounded = Math.round(props.selection.avgGradientPercent);
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
});
const formattedDuration = computed(() => new PrettyTime(props.selection.estDurationSeconds).prettyTime());
</script>

<style scoped>
.selection-stats-panel {
  /* Non-zero flex-basis so the parent header row (`.epv2-header-row`) wraps
     this whole panel onto its own line on narrow/mobile widths, rather than
     placing it on line 1 (a `flex: 1` / 0-basis item always "fits" there)
     and then squeezing it down — see the matching comment on
     `.epv2-stat-cluster` in ElevationPanelV2.vue for the full explanation. */
  flex: 1 1 220px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

/* Stats wrap on their own, independently of `.selection-reset` — keeping the
   reset button out of the wrapping flow means it stays pinned at the row's
   trailing edge (vertically centred against however many stat lines wrap)
   instead of `margin-left: auto` yanking it to the far right of whichever
   line it happens to land on when it wraps too, which left a jarring gap. */
.selection-stats {
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  min-width: 0;
}

.selection-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
}

.selection-stat .material-icons {
  font-size: 15px;
  color: #ffdf46;
}

.selection-reset {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
}

.selection-reset:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}
</style>
